import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useOpenState from '../../../../../hooks/useOpenState';
import {hasOneOfPermission} from '../../../../../store/reducers/user';
import _ from 'lodash';
import {setSnackbar} from '../../../../../store/reducers/globals';
import {useTranslation} from 'react-i18next';
import MessageBox from '../../../../../util/messageBox';
import ErrorBoundary from '../../../../../controls/errorBoundary';
import FormErrorPage from '../../../../error/customErrorPages/formErrorPage';
import {closePatientForm, ctxMiniFormsQueue, ctxSplitScreen} from '../../../../../store/reducers/patientContext';
import {nextWorkflowStep, workflow} from '../../../../../store/reducers/workflow';

const ContextFormWrapper = (props) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // Props
    const {form, handleSaveData, screen, ...restProps} = props;

    // Selectors
    const formQueue = useSelector(ctxMiniFormsQueue);
    const workflowData = useSelector(workflow);
    const splitScreen = useSelector(ctxSplitScreen);

    // Form state
    const [isFormOpen, openForm, closeForm] = useOpenState(false, true);
    const [isWarningOpen, openWarning, closeWarning] = useOpenState(false, true);
    const tempData = useRef(null);
    const edited = useRef(false);

    useEffect(() => {
        if (!_.isEmpty(formQueue) && form?.id === _.last(formQueue)) {
            if ((splitScreen.enabled && splitScreenQueueExists(form?.id)) || !splitScreen?.enabled) {
                if (_.isEmpty(form.requiredPermission) || (!_.isEmpty(form.requiredPermission) && dispatch(hasOneOfPermission(form.requiredPermission)))) {
                    openForm();
                } else {
                    dispatch(setSnackbar({
                        type: 'warning',
                        message: t('User does not have permission to open this form.'),
                    }));
                }
            }
        }
    }, [formQueue, form, splitScreen?.enabled]);

    function splitScreenQueueExists(formId) {
        const queue = screen === 1 ? splitScreen.screenOneForms : splitScreen.screenTwoForms;
        return _.includes(queue, formId);
    }

    const handleCloseMiniForm = (val) => {
        if (edited.current) {
            tempData.current = val;
            openWarning();
        } else {
            handleSaveData(val);
            if (tempData.current) tempData.current = null;
            handleCloseForm();
        }
    };

    const handleModifyForm = () => {
        edited.current = true;
    };

    const handleCloseWarning = () => {
        tempData.current = null;
        closeWarning();
    };

    const handleForceClose = () => {
        edited.current = false;
        handleCloseMiniForm(tempData.current);
        closeWarning();
        handleCloseForm();
    };

    const handleCloseForm = (_e, reason) => {
        if (reason !== 'backdropClick') {
            closeForm();
            dispatch(closePatientForm(form.id));
            if (workflowData?.type === form?.id) {
                dispatch(nextWorkflowStep());
            }
        }
    };

    if (!isFormOpen) return null;

    return (
        <>
            <ErrorBoundary errorPage={<FormErrorPage/>}>
                {React.cloneElement(form?.context, {
                    open: isFormOpen,
                    onClose: handleCloseForm,
                    onChange: handleCloseMiniForm,
                    onModify: handleModifyForm,
                    _formTitle: form?.formTitle || null,
                    _formOptions: {
                        ...form.options,
                        containerRef: props.containerRef,
                    },
                    ...restProps,
                })}
            </ErrorBoundary>

            <MessageBox
                open={isWarningOpen}
                onClose={handleCloseWarning}
                title={t('Warning')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleForceClose}
                message={t('It looks like you have edited something. If you leave form before saving, your changes will be lost.')}
            />
        </>
    );
};

export default ContextFormWrapper;
